import DataTable from 'datatables.net-bs5';
import * as Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
// v12: highcharts-more self-registers on side-effect import (HighchartsMore factory removed — BC-01).
// The per-farmer completion radar uses chart.polar:true (powered by highcharts-more); 'arearange' is a
// canonical highcharts-more series type, used here as the module-registration sentinel (D-04 loud guard).
// NOTE: 'spiderweb' is NOT a Highcharts series type — the radar is a polar line chart, not a spiderweb series.
if (typeof (Highcharts as any).seriesTypes?.['arearange'] === 'undefined') {
    console.error('[highcharts-more] module failed to register (arearange missing) — the polar radar chart will not render. Check the highcharts-more side-effect import.');
}
import { init as avatarInit } from './stardew-avatar';
import { post, notifier } from './util';
import { getChartTheme } from './utils/chartTheme';
declare var sv_completion_data: Array<{
    farmer_name: string;
    spokes: number[];
    raw: {
        shipped: number; fish: number; crab_pot: number; cooked: number; crafted: number;
        museum: number; achievements: number; walnuts: number; notes: number; scraps: number;
    };
}> | undefined;
declare var sv_activity_data: Array<{
    farmer_name: string;
    buckets: Record<string, number>;
    total: number;
    bedtime_raw: number;
    bedtime_str: string;
    days_played: number;
    ms_played: number;
    ms_played_str: string;
    min_per_day: number;
    min_per_day_str: string;
}> | undefined;
declare var sv_history_sparklines: Array<{ label: string; series: number[]; latest: number; delta: number }>;
declare var sv_history_farm_timeline: Array<{ label: string; gold_earned: number; days_played: number }>;
declare var sv_history_milestones: Array<{ label: string; items_shipped_count: number; museum_count: number; fish_species_count_rod: number; crab_pot_species_count: number }>;
declare var sv_history_farmer_trends: Record<string, {
    snapshots: string[];
    skills: Record<string, number[]>;
    skills_xp: Record<string, number[]>;
    max_health: number[];
    max_energy: (number | null)[];
    stats: Record<string, number[]>;
    friendships_marriage: Record<string, number[]>;
    friendships_other: Record<string, number[]>;
    fish_species: number[];
    fish_total_rod: number[];   // Phase 77: rod-caught fish only (excludes 10 crab-pot species)
    fish_total_crab: number[];  // Phase 77: crab-pot catches only
}>;

export default class {
    static init = () => {
        leaderboard();
        leaderboardTables();
        farmTables();
        farmHistory();
        diffViewer();
        avatarInit();
        completionRadar();
        activityDonut();
        attachCustomNameEditor();
        initRelativeTimes();
    }
}

// Browser-local absolute tooltip formatter — mirrors shared.ts formatTooltip so the
// "Last played" hover reads in the viewer's timezone with a tz name (D-08), not UTC.
const TOOLTIP_FMT = new Intl.DateTimeFormat('en', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZoneName: 'short',
});

function initRelativeTimes(): void {
    const now = Date.now();
    document.querySelectorAll<HTMLElement>('.sv-relative-time').forEach((el) => {
        const iso = el.dataset.time;
        if (!iso) return;
        const date = new Date(iso);
        if (isNaN(date.getTime())) return; // invalid ISO — leave server fallback title
        const diff = Math.floor((now - date.getTime()) / 1000);
        el.textContent = relativeTime(diff);
        el.title = TOOLTIP_FMT.format(date); // browser-local (was a frozen UTC Bootstrap tooltip)
    });
}

function relativeTime(seconds: number): string {
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
    const years = Math.floor(days / 365);
    return `${years} year${years === 1 ? '' : 's'} ago`;
}

function leaderboard(): void {
    const tabEl = document.getElementById('sv-leaderboard-tabs');
    if (!tabEl) return;  // guard: only run on the leaderboard page

    const farmSelect = document.getElementById('sv-farm-select') as HTMLSelectElement | null;
    if (!farmSelect) return;

    farmSelect.addEventListener('change', () => {
        const farmId = farmSelect.value;
        if (!farmId) {
            // user picked the placeholder — navigate to leaderboard without filter
            window.location.href = '/stardew/leaderboard';
            return;
        }
        window.location.href = '/stardew/leaderboard?farm_id=' + encodeURIComponent(farmId);
    });
}

function leaderboardTables(): void {
    // Only run on the leaderboard page
    if (!document.getElementById('sv-leaderboard-tabs')) return;

    const allFarms = document.querySelector('.datatable[data-table="sv-leaderboard-all"]') as HTMLTableElement | null;
    if (allFarms) {
        new DataTable(allFarms, {
            paging: false,
            searching: false,
            info: false,
            order: [],          // respect server-side sort (gold_earned DESC, farm_name ASC)
        });
    }

    const singleFarm = document.querySelector('.datatable[data-table="sv-leaderboard-single"]') as HTMLTableElement | null;
    if (singleFarm) {
        new DataTable(singleFarm, {
            paging: false,
            searching: false,
            info: false,
            order: [],          // respect server-side sort (is_host DESC, gold DESC)
        });
    }
}

function wireSectionSearch(
    inputSel: string,
    tableSel: string,
    dtOptions: Record<string, unknown> = {},
): void {
    const input = document.querySelector<HTMLInputElement>(inputSel);
    if (!input) return;
    const instances: InstanceType<typeof DataTable>[] = [];
    document.querySelectorAll<HTMLTableElement>(tableSel).forEach((el) => {
        instances.push(new DataTable(el, {
            paging: false,
            searching: true,
            info: false,
            dom: 't',
            ...dtOptions,
        }));
    });
    if (instances.length === 0) return;
    let timer: ReturnType<typeof setTimeout>;
    input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            instances.forEach(dt => dt.search(input.value).draw());
        }, 200);
    });
}

// Phase 74.1 — plain-text pill filter replacing DataTables quest search.
// Hides non-matching pills, collapses empty type-group headers, hides empty farmer columns.
// Fix: pills use d-flex !important so style.display can't hide them — toggle d-none class instead.
// Fix: search against data-search (title|npc) so NPC name searches (e.g. "Robin") also work.
function wireQuestPillSearch(): void {
    const input = document.querySelector<HTMLInputElement>('#sv-search-quests');
    if (!input) return;

    let timer: ReturnType<typeof setTimeout>;
    input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            const term = input.value.trim().toLowerCase();
            document.querySelectorAll<HTMLElement>('.sv-quest-farmer-col').forEach((col) => {
                let colHasMatch = false;
                col.querySelectorAll<HTMLElement>('.sv-quest-type-group').forEach((group) => {
                    let groupHasMatch = false;
                    group.querySelectorAll<HTMLElement>('.sv-quest-pill').forEach((pill) => {
                        // data-search = "title|npc_name" (set in farm.latte); fall back to textContent.
                        const text = (pill.dataset.search ?? pill.textContent ?? '').toLowerCase();
                        const match = term === '' || text.includes(term);
                        // Pills carry d-flex !important — inline style.display cannot override it.
                        // Toggle d-none class instead (also !important, but declared after d-flex in Bootstrap).
                        pill.classList.toggle('d-none', !match);
                        if (match) { groupHasMatch = true; colHasMatch = true; }
                    });
                    group.classList.toggle('d-none', !groupHasMatch);
                });
                col.classList.toggle('d-none', !colHasMatch);
            });
        }, 200);
    });
}

function farmTables(): void {
    // Only run on the farm detail page
    if (!document.getElementById('sv-farm-detail')) return;

    // Friendships and recipes (cooking/crafting) DataTable inits removed in Phase 39 — those tables are now responsive card grids in farm.latte.

    // Section-level cross-farmer search for Items Shipped, Fish Caught, Crafted, and Combat
    wireSectionSearch('#sv-search-shipped', '.datatable[data-table^="sv-items-shipped-"]', { order: [[1, 'desc']] });
    wireSectionSearch('#sv-search-fish', '.datatable[data-table^="sv-fish-"]', { order: [[1, 'desc']] });
    wireSectionSearch('#sv-search-crab-pot', '.datatable[data-table^="sv-crab-"]', { order: [[1, 'desc']] });
    wireSectionSearch('#sv-search-crafted', '.datatable[data-table^="sv-crafted-"]', { order: [[1, 'desc']] });
    wireSectionSearch('#sv-search-combat', '.datatable[data-table^="combat-monsters-"]', { order: [[1, 'desc']] });
    wireSectionSearch('#sv-search-minerals', '.datatable[data-table^="sv-minerals-"]', { order: [[1, 'desc']] });
    wireSectionSearch('#sv-search-crops', '.datatable[data-table^="sv-crops-"]', { order: [[1, 'desc']] });
    wireQuestPillSearch();
    socialShowAllToggle();
}

// Phase 109 Plan 05 — D-07 "Show all villagers" toggle.
// Pure DOM class swap on the Social accordion grid; per-page state (no localStorage).
// Default state: grid carries .sv-social-hide-unmet → CSS hides [data-met="0"] cards.
// Click removes the class → unmet cards become visible. Button label flips accordingly.
// Modelled on wireQuestPillSearch (typed selectors, early-return guards, no any).
function socialShowAllToggle(): void {
    const btn = document.querySelector<HTMLButtonElement>('[data-sv-toggle="social-show-all"]');
    if (!btn) return;
    const grid = btn.closest('.accordion-body')?.querySelector<HTMLElement>('.sv-social-grid');
    if (!grid) return;

    const labelShow = btn.dataset.labelShow ?? 'Show all villagers';
    const labelHide = btn.dataset.labelHide ?? 'Hide unmet';

    // Initial-state sync: when grid already hides unmet, button must offer "Show all".
    btn.textContent = grid.classList.contains('sv-social-hide-unmet') ? labelShow : labelHide;

    btn.addEventListener('click', () => {
        const hidden = grid.classList.toggle('sv-social-hide-unmet');
        btn.textContent = hidden ? labelShow : labelHide;
    });
}

// ── Phase 73: Per-farmer completion radar dashboard ──────────────────────────

// Phase 123.1-04 — Post-fragment chart initialiser.
// After innerHTML swap, <script> tags are NOT executed. Chart data for the
// completion radar and activity donut is passed via data-* attributes on a
// hidden .sv-chart-data sentinel element inside the partial. This function
// reads those attributes and calls the chart-init functions.
function initFragmentCharts(container: Element): void {
    const sentinel = container.querySelector('.sv-chart-data') as HTMLElement | null;
    if (!sentinel) return;

    const completionJson = sentinel.dataset.completionJson;
    if (completionJson) {
        try {
            const data = JSON.parse(completionJson);
            if (Array.isArray(data)) completionRadarWithData(data);
        } catch (_) { /* malformed JSON — skip */ }
    }

    const activityJson = sentinel.dataset.activityJson;
    if (activityJson) {
        try {
            const data = JSON.parse(activityJson);
            if (Array.isArray(data)) activityDonutWithData(data);
        } catch (_) { /* malformed JSON — skip */ }
    }
}

function completionRadar(): void {
    if (typeof sv_completion_data === 'undefined' || !Array.isArray(sv_completion_data)) {
        return;
    }
    completionRadarWithData(sv_completion_data);
}

function completionRadarWithData(sv_completion_data: Array<{farmer_name: string, spokes: number[], raw: Record<string, number>}>): void {

    // Phase 77: Fish spoke split into Fish (rod /64) + Crab Pot (/10) — 10 spokes total.
    const SPOKE_LABELS = [
        'Shipping', 'Fish', 'Crab Pot', 'Cooking', 'Crafting',
        'Museum', 'Achievements', 'Walnuts', 'Secret Notes', 'Journal Scraps',
    ];
    const TOTALS = [804, 64, 10, 81, 150, 95, 39, 130, 27, 11];

    sv_completion_data.forEach((farmer, i) => {
        const elId = `sv-completion-radar-${i}`;
        const el = document.getElementById(elId);
        if (!el) return;

        const rawVals = [
            farmer.raw.shipped, farmer.raw.fish, farmer.raw.crab_pot,
            farmer.raw.cooked, farmer.raw.crafted,
            farmer.raw.museum, farmer.raw.achievements,
            farmer.raw.walnuts, farmer.raw.notes, farmer.raw.scraps,
        ];

        Highcharts.chart(elId, Highcharts.merge(getChartTheme(), {
            chart: { polar: true, type: 'line', height: 320, animation: false },
            title: { text: '' },
            legend: { enabled: false },
            credits: { enabled: false },
            xAxis: {
                categories: SPOKE_LABELS,
                tickmarkPlacement: 'on',
                lineWidth: 0,
            },
            yAxis: {
                gridLineInterpolation: 'polygon',
                lineWidth: 0,
                min: 0,
                max: 100,
                tickInterval: 25,
                labels: {
                    formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
                        return this.value + '%';
                    },
                },
            },
            tooltip: {
                shared: true,
                pointFormatter(this: Highcharts.Point): string {
                    const idx = this.index;
                    return `<b>${SPOKE_LABELS[idx]}</b>: ${Number(this.y).toFixed(1)}% (${rawVals[idx]}/${TOTALS[idx]})<br/>`;
                },
            },
            series: [{
                type: 'line',
                name: farmer.farmer_name,
                data: farmer.spokes,
                pointPlacement: 'on',
            }],
        } as any));

        const tableEl = document.getElementById(`sv-completion-table-${i}`);
        if (tableEl) {
            const rows = SPOKE_LABELS
                .map((label, idx) => ({
                    label,
                    pct: Number(farmer.spokes[idx]) || 0,
                    raw: rawVals[idx],
                    total: TOTALS[idx],
                }))
                .sort((a, b) => b.pct - a.pct);
            tableEl.innerHTML =
                '<table class="table table-sm table-striped mb-0"><tbody>' +
                rows.map(r =>
                    `<tr><td>${r.label}</td>` +
                    `<td class="text-end">${r.pct.toFixed(1)}%</td>` +
                    `<td class="text-end text-muted small">${r.raw}/${r.total}</td></tr>`
                ).join('') +
                '</tbody></table>';
        }
    });
}

// ── Phase 76 — Activity donut per farmer ────────────────────────────────────

function activityDonut(): void {
    if (typeof sv_activity_data === 'undefined' || !Array.isArray(sv_activity_data)) return;
    activityDonutWithData(sv_activity_data);
}

function activityDonutWithData(sv_activity_data: Array<{farmer_name: string, total: number, buckets: Record<string, number>}>): void {
    const BUCKET_COLORS: Record<string, string> = {
        Mines:    '#a0522d',
        Farm:     '#5cb85c',
        Forest:   '#27ae60',
        Beach:    '#3498db',
        Town:     '#f39c12',
        Workshop: '#9b59b6',
        Other:    '#95a5a6',
    };

    sv_activity_data.forEach((farmer, i) => {
        const elId = `sv-activity-donut-${i}`;
        const el = document.getElementById(elId);
        if (!el || farmer.total === 0) return;  // zero-total guard (research Pitfall 3)

        const seriesData = Object.entries(farmer.buckets)
            .filter(([_, val]) => val > 0)
            .map(([name, val]) => ({ name, y: val, color: BUCKET_COLORS[name] || '#888' }));

        Highcharts.chart(elId, Highcharts.merge(getChartTheme(), {
            chart: { type: 'pie', height: 300, animation: false },
            title: { text: '' },
            credits: { enabled: false },
            legend: { enabled: true },
            tooltip: { pointFormat: '<b>{point.name}</b>: {point.y} actions ({point.percentage:.1f}%)' },
            plotOptions: { pie: { innerSize: '55%', dataLabels: { enabled: false }, showInLegend: true } },
            series: [{ type: 'pie', name: farmer.farmer_name, data: seriesData }],
        } as any));
    });
}

// ── Save History page ────────────────────────────────────────────────────────

const SV_LEVEL_XP_THRESHOLDS = [100, 380, 770, 1300, 2150, 3300, 4800, 6900, 10000, 15000];

function farmHistory(): void {
    if (!document.getElementById('sv-farm-history')) return;

    // ── Sparklines ──────────────────────────────────────────────────────────

    if (typeof sv_history_sparklines !== 'undefined') {
        sv_history_sparklines.forEach((spark, i) => {
            const elId = 'sv-spark-' + i;
            const el = document.getElementById(elId);
            if (!el || !spark.series || spark.series.length === 0) return;

            Highcharts.chart(elId, Highcharts.merge(getChartTheme(), {
                chart: {
                    type: 'area',
                    height: 30,
                    margin: [2, 0, 2, 0],
                    animation: false,
                },
                title: { text: undefined },
                credits: { enabled: false },
                legend: { enabled: false },
                tooltip: { enabled: false },
                xAxis: { visible: false },
                yAxis: { visible: false, startOnTick: false, endOnTick: false },
                plotOptions: {
                    area: {
                        lineWidth: 1,
                        color: '#ffa94d',
                        fillOpacity: 0.15,
                        marker: { enabled: false },
                        animation: false,
                    },
                },
                series: [{ type: 'area', data: spark.series }],
            } as any));
        });
    }

    // ── Farm timeline line charts ────────────────────────────────────────────

    // lineChartDefaults provides chart-specific (non-chrome) structure only.
    // Chrome (bg/fg/gridLine colors) comes from getChartTheme() via Highcharts.merge at each call site.
    const lineChartDefaults = (title: string) => ({
        chart: { type: 'line', height: 270, animation: false },
        title: { text: undefined },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
            labels: { rotation: -35 },
            title: { text: undefined },
        },
        yAxis: {
            title: { text: title },
            allowDecimals: false,
            gridLineWidth: 0.1,
        },
        plotOptions: {
            line: {
                marker: { enabled: true, radius: 4 },
                animation: false,
            },
        },
    });

    // Gold chart
    if (typeof sv_history_farm_timeline !== 'undefined' && sv_history_farm_timeline.length > 0) {
        const goldEl = document.getElementById('sv-chart-gold');
        if (goldEl) {
            Highcharts.chart('sv-chart-gold', Highcharts.merge(getChartTheme(), {
                ...lineChartDefaults('Gold'),
                xAxis: {
                    categories: sv_history_farm_timeline.map(d => d.label),
                    labels: { rotation: -35 },
                    title: { text: undefined },
                },
                tooltip: {
                    formatter: function (this: any): string {
                        return `<b>${this.x}</b><br/>Gold: ${Number(this.y).toLocaleString()}g`;
                    },
                },
                series: [{
                    type: 'line',
                    name: 'Gold Earned',
                    color: '#ffd700',
                    data: sv_history_farm_timeline.map(d => d.gold_earned),
                }],
            } as any));
        }
    }

    // Items shipped chart
    if (typeof sv_history_milestones !== 'undefined' && sv_history_milestones.length > 0) {
        const shippedEl = document.getElementById('sv-chart-shipped');
        if (shippedEl) {
            Highcharts.chart('sv-chart-shipped', Highcharts.merge(getChartTheme(), {
                ...lineChartDefaults('Items Shipped'),
                xAxis: {
                    categories: sv_history_milestones.map(d => d.label),
                    labels: { rotation: -35 },
                    title: { text: undefined },
                },
                tooltip: {
                    formatter: function (this: any): string {
                        return `<b>${this.x}</b><br/>Items Shipped: ${Number(this.y).toLocaleString()}`;
                    },
                },
                series: [{
                    type: 'line',
                    name: 'Items Shipped',
                    color: '#51cf66',
                    data: sv_history_milestones.map(d => d.items_shipped_count),
                }],
            } as any));
        }

        // Milestones chart — museum donations over time
        const milestonesEl = document.getElementById('sv-chart-milestones');
        if (milestonesEl) {
            Highcharts.chart('sv-chart-milestones', Highcharts.merge(getChartTheme(), {
                ...lineChartDefaults('Museum Donations'),
                xAxis: {
                    categories: sv_history_milestones.map(d => d.label),
                    labels: { rotation: -35 },
                    title: { text: undefined },
                },
                tooltip: {
                    formatter: function (this: any): string {
                        return `<b>${this.x}</b><br/>Museum Donations: ${Number(this.y).toLocaleString()}`;
                    },
                },
                series: [{
                    type: 'line',
                    name: 'Museum',
                    color: '#cc5de8',
                    data: sv_history_milestones.map(d => d.museum_count),
                }],
            } as any));
        }
    }

    // ── Per-farmer trend charts ──────────────────────────────────────────────

    if (typeof sv_history_farmer_trends !== 'undefined') {
        const skillColors: Record<string, string> = {
            farming: '#6aa84f',
            mining: '#999999',
            foraging: '#3d9970',
            fishing: '#339af0',
            combat: '#e03131',
        };

        Object.entries(sv_history_farmer_trends).forEach(([, trend], i) => {
            const cats = trend.snapshots;
            if (!cats || cats.length === 0) return;

            const farmerLineDefaults = (yTitle: string) => ({
                ...lineChartDefaults(yTitle),
                xAxis: {
                    categories: cats,
                    labels: { rotation: -35 },
                    title: { text: undefined },
                },
            });

            // Skills chart — multi-series XP line with level-threshold plotLines
            const skillsEl = document.getElementById(`sv-fh-skills-${i}`);
            if (skillsEl && trend.skills_xp) {
                const skillSeries = Object.entries(trend.skills_xp).map(([skillName, data]) => ({
                    type: 'line' as const,
                    name: skillName.charAt(0).toUpperCase() + skillName.slice(1),
                    color: skillColors[skillName] ?? '#888',
                    data,
                }));
                Highcharts.chart(`sv-fh-skills-${i}`, Highcharts.merge(getChartTheme(), {
                    ...farmerLineDefaults('XP'),
                    yAxis: {
                        ...farmerLineDefaults('XP').yAxis,
                        plotLines: SV_LEVEL_XP_THRESHOLDS.map((xp, idx) => ({
                            value: xp,
                            color: 'rgba(150,150,150,0.35)',
                            dashStyle: 'ShortDash',
                            width: 1,
                            label: { text: `Lvl ${idx + 1}`, align: 'right', x: -4, style: { fontSize: '9px' } },
                        })),
                    },
                    legend: { enabled: true },
                    tooltip: {
                        shared: true,
                        formatter: function (this: any): string {
                            let s = `<b>${this.x}</b>`;
                            this.points?.forEach((p: any) => {
                                const lvlArr = trend.skills[p.series.name.toLowerCase()];
                                const lvl = lvlArr ? lvlArr[p.point.index] : '?';
                                s += `<br/>${p.series.name}: <b>Level ${lvl}</b> (${Number(p.y).toLocaleString()} XP)`;
                            });
                            return s;
                        },
                    },
                    series: skillSeries,
                } as any));
            }

            // Single-series farmer chart helper (fish species + stat counters)
            const singleFarmerChart = (
                suffix: string,
                yTitle: string,
                seriesName: string,
                color: string,
                data: number[] | undefined,
            ) => {
                const el = document.getElementById(`sv-fh-${suffix}-${i}`);
                if (!el) return;
                if (!data || data.length === 0) {
                    el.innerHTML = `<p class="text-muted small p-2 mb-0">No ${seriesName.toLowerCase()} history available.</p>`;
                    return;
                }
                Highcharts.chart(`sv-fh-${suffix}-${i}`, Highcharts.merge(getChartTheme(), {
                    ...farmerLineDefaults(yTitle),
                    tooltip: {
                        formatter: function (this: any): string {
                            return `<b>${this.x}</b><br/>${seriesName}: ${Number(this.y).toLocaleString()}`;
                        },
                    },
                    series: [{ type: 'line', name: seriesName, color, data }],
                } as any));
            };

            // Fish species + total fish + the three stat counters — one chart each
            singleFarmerChart('fish', 'Species', 'Fish Species', '#339af0', trend.fish_species);
            singleFarmerChart('fish-total', 'Count', 'Total Fish Caught (Rod)', '#20c997', trend.fish_total_rod);
            singleFarmerChart('fish-total-crab', 'Count', 'Total Crab Pot Catches', '#ff9f43', trend.fish_total_crab);
            singleFarmerChart('steps', 'Count', 'Steps Taken', '#ffa94d', trend.stats?.stepsTaken);
            singleFarmerChart('gifts', 'Count', 'Gifts Given', '#f06595', trend.stats?.giftsGiven);
            singleFarmerChart('monsters', 'Count', 'Monsters Killed', '#e03131', trend.stats?.monstersKilled);

            // NPC friendship charts — marriage candidates and other villagers (hearts)
            const friendshipChart = (suffix: string, friendships: Record<string, number[]>) => {
                const el = document.getElementById(`sv-fh-${suffix}-${i}`);
                if (!el) return;
                const series = Object.entries(friendships ?? {}).map(([npcName, data]) => ({
                    type: 'line' as const,
                    name: npcName,
                    data,
                }));
                if (series.length === 0) {
                    el.innerHTML = '<p class="text-muted small p-2 mb-0">No friendship history available.</p>';
                    return;
                }
                Highcharts.chart(`sv-fh-${suffix}-${i}`, Highcharts.merge(getChartTheme(), {
                    ...farmerLineDefaults('Hearts'),
                    legend: {
                        enabled: true,
                        itemStyle: { fontSize: '10px' },
                        verticalAlign: 'bottom',
                        maxHeight: 52,
                    },
                    tooltip: {
                        shared: true,
                        formatter: function (this: any): string {
                            let s = `<b>${this.x}</b>`;
                            this.points?.forEach((p: any) => {
                                s += `<br/>${p.series.name}: ${p.y} ♥`;
                            });
                            return s;
                        },
                    },
                    series,
                } as any));
            };
            friendshipChart('marriage', trend.friendships_marriage);
            friendshipChart('other', trend.friendships_other);

            // Max Health & Energy chart — two-series line with legend
            const healthEl = document.getElementById(`sv-fh-health-${i}`);
            if (healthEl && trend.max_health && trend.max_health.length > 0) {
                Highcharts.chart(`sv-fh-health-${i}`, Highcharts.merge(getChartTheme(), {
                    ...farmerLineDefaults('HP / Energy'),
                    legend: { enabled: true },
                    tooltip: {
                        shared: true,
                        formatter: function (this: any): string {
                            let s = `<b>${this.x}</b>`;
                            this.points?.forEach((p: any) => {
                                if (p.y !== null && p.y !== undefined) {
                                    s += `<br/>${p.series.name}: ${p.y}`;
                                }
                            });
                            return s;
                        },
                    },
                    series: [
                        { type: 'line', name: 'Max Health', color: '#e03131', data: trend.max_health },
                        { type: 'line', name: 'Max Energy', color: '#40c057', data: trend.max_energy ?? [] },
                    ],
                } as any));
            }
        });
    }
}

// -------------------------------------------------------------------------
// Phase 80: Inline custom farm name editor
// -------------------------------------------------------------------------

type SetCustomNameResponse = { ok: boolean; display_name?: string; error?: string };

function attachCustomNameEditor(): void {
    const editors = document.querySelectorAll<HTMLElement>('[data-farm-id]');
    editors.forEach((el) => {
        const pencilBtn = el.querySelector<HTMLButtonElement>('.js-edit-farm-name');
        const displaySpan = el.querySelector<HTMLSpanElement>('.js-farm-name-display');
        if (!pencilBtn || !displaySpan) return;

        pencilBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const originalText = displaySpan.textContent ?? '';

            // Build editor DOM
            const input = document.createElement('input');
            input.type = 'text';
            input.className = 'form-control form-control-sm d-inline-block';
            input.style.width = 'auto';
            input.maxLength = 64;
            input.value = originalText;

            const saveBtn = document.createElement('button');
            saveBtn.type = 'button';
            saveBtn.className = 'btn btn-sm btn-success ms-1 js-save-name';
            saveBtn.textContent = '✓';

            const cancelBtn = document.createElement('button');
            cancelBtn.type = 'button';
            cancelBtn.className = 'btn btn-sm btn-secondary ms-1 js-cancel-name';
            cancelBtn.textContent = '✕';

            // Swap display span content with editor
            displaySpan.textContent = '';
            displaySpan.appendChild(input);
            displaySpan.appendChild(saveBtn);
            displaySpan.appendChild(cancelBtn);

            input.focus();
            input.select();

            const cancel = () => {
                displaySpan.textContent = originalText;
                pencilBtn.style.display = '';
            };

            const submit = () => {
                const farmId = el.getAttribute('data-farm-id');
                const newName = input.value;

                input.disabled = true;
                saveBtn.disabled = true;
                saveBtn.innerHTML = '<span class="spinner-border spinner-border-sm"></span>';

                post('?do=setCustomName', { id: farmId, name: newName }, (response: SetCustomNameResponse) => {
                    if (response.ok === true && response.display_name !== undefined) {
                        displaySpan.textContent = response.display_name;
                        pencilBtn.style.display = '';
                    } else {
                        input.disabled = false;
                        saveBtn.disabled = false;
                        saveBtn.textContent = '✓';
                        notifier.warning(response?.error ?? 'Could not save');
                    }
                });
            };

            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') { e.preventDefault(); submit(); }
                if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            });

            saveBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); submit(); });
            cancelBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); cancel(); });

            pencilBtn.style.display = 'none';
        });
    });
}

// ─── Phase 85: Two-snapshot diff viewer ─────────────────────────────────────
// Binds change listeners to #sv-diff-a / #sv-diff-b, AJAX-fetches the fragment
// from data-fragment-url (server-injected via Latte {link diffFragment}), swaps
// innerHTML on #sv-diff-result, persists ?a=&b= via history.replaceState, and
// filters B-options so only post-A snapshots remain selectable (Pitfall 6).
// CRITICAL: util.ts::get does response.json() which cannot be reused here —
// the fragment endpoint returns HTML text. Raw fetch().text() is intentional.
function diffViewer(): void {
    const result = document.getElementById('sv-diff-result');
    if (!result) return;  // guard: only run on Save History page with picker present

    const aSelect = document.getElementById('sv-diff-a') as HTMLSelectElement | null;
    const bSelect = document.getElementById('sv-diff-b') as HTMLSelectElement | null;
    const apartLabel = document.getElementById('sv-diff-apart');
    const fragmentUrl = result.dataset.fragmentUrl;

    if (!aSelect || !bSelect || !fragmentUrl) return;

    function getEpoch(select: HTMLSelectElement): number {
        const opt = select.selectedOptions[0];
        if (!opt) return 0;
        return parseInt(opt.dataset.parsedAt ?? '0', 10);
    }

    function updateApartLabel(): void {
        if (!apartLabel || !aSelect || !bSelect) return;
        const aEpoch = getEpoch(aSelect);
        const bEpoch = getEpoch(bSelect);
        if (aEpoch > 0 && bEpoch > 0 && bEpoch > aEpoch) {
            const days = Math.floor((bEpoch - aEpoch) / 86400);
            apartLabel.textContent = `${days} day${days === 1 ? '' : 's'} apart`;
        } else {
            apartLabel.textContent = '— days apart';
        }
    }

    function filterBOptions(): void {
        if (!aSelect || !bSelect) return;
        const aEpoch = getEpoch(aSelect);
        Array.from(bSelect.options).forEach((opt) => {
            const bEpoch = parseInt(opt.dataset.parsedAt ?? '0', 10);
            opt.hidden = bEpoch <= aEpoch;
        });
        // If current B selection is now hidden, snap B to the latest still-valid option.
        const currentOpt = bSelect.selectedOptions[0];
        if (!currentOpt || currentOpt.hidden) {
            const valid = Array.from(bSelect.options).filter((o) => !o.hidden);
            if (valid.length) {
                bSelect.value = valid[valid.length - 1].value;
            }
        }
    }

    async function loadDiff(): Promise<void> {
        if (!aSelect || !bSelect || !fragmentUrl) return;
        const a = aSelect.value;
        const b = bSelect.value;
        if (!a || !b || a === b) return;  // D-04: A != B by construction

        const url = new URL(fragmentUrl, window.location.origin);
        url.searchParams.set('a', a);
        url.searchParams.set('b', b);

        try {
            const resp = await fetch(url.toString(), {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            const html = await resp.text();  // HTML fragment, NOT JSON (anti-pattern doc'd in 85-RESEARCH Pattern 2)
            result!.innerHTML = html;
        } catch (err) {
            console.error('diffViewer: fetch failed', err);
            return;
        }

        // D-02 URL persistence: sync ?a=&b= so refresh/share/back-button restore picks.
        const u = new URL(window.location.href);
        u.searchParams.set('a', a);
        u.searchParams.set('b', b);
        history.replaceState(null, '', u.toString());
    }

    aSelect.addEventListener('change', () => {
        filterBOptions();
        updateApartLabel();
        void loadDiff();
    });
    bSelect.addEventListener('change', () => {
        updateApartLabel();
        void loadDiff();
    });

    const clearBtn = document.getElementById('sv-diff-clear');
    clearBtn?.addEventListener('click', () => {
        if (!aSelect || !bSelect) return;
        const firstOpt = aSelect.options[0];
        const lastOpt = bSelect.options[bSelect.options.length - 1];
        if (firstOpt) aSelect.value = firstOpt.value;
        if (lastOpt) bSelect.value = lastOpt.value;
        Array.from(bSelect.options).forEach((opt) => { opt.hidden = false; });
        filterBOptions();
        updateApartLabel();
        result!.innerHTML = '';
        const u = new URL(window.location.href);
        u.searchParams.delete('a');
        u.searchParams.delete('b');
        history.replaceState(null, '', u.pathname + (u.search ? u.search : '') + u.hash);
    });

    // Initial paint: hide invalid B options + render the days-apart label.
    // Do NOT auto-fire loadDiff() on init — when ?a=&b= are present, the presenter
    // already server-rendered the initial fragment (Phase 83 D-03 / Phase 85 D-02).
    filterBOptions();
    updateApartLabel();
}

// Phase 123.1 — Delegated lazy-load handler for farm-page accordion sections.
// ONE handler covers ALL deferred accordions (D-03: one-handler-covers-all-accordions contract).
// Mirrors diffViewer()'s fetch().text() → innerHTML idiom (raw HTML, NOT JSON — intentional;
// util.ts::get() does .json() which cannot be reused here).
//
// Event topology:
//   - shown.bs.collapse fires on the div.accordion-collapse pane (not the wrapper or button)
//   - Must .closest('[data-fragment-url]') to walk up to the div.accordion wrapper
//   - data-fragment-url and data-section sit on the outermost div.accordion element (Pitfall 1)
//
// Fetch-once contract:
//   - Set dataset.loaded = '1' BEFORE await (optimistic lock — prevents double-fire on rapid
//     open/close; Pitfall 4)
//   - Reset dataset.loaded = '' ONLY on failure so retry works
//
// Error UX: inline "Couldn't load — Retry" link (D-04) — NOT silent console.error because
// deferred content is the primary accordion content.
document.addEventListener('shown.bs.collapse', async (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    // shown.bs.collapse fires on div.accordion-collapse; walk up to the accordion wrapper
    // that carries data-fragment-url (Pitfall 1 — event does NOT fire on the wrapper itself)
    const accordion = target.closest('[data-fragment-url]') as HTMLElement | null;
    if (!accordion) return;

    // fetch-once guard — skip if already loaded or loading
    if (accordion.dataset.loaded === '1') return;

    const fragmentUrl = accordion.dataset.fragmentUrl;
    if (!fragmentUrl) return;

    const body = target.querySelector('.accordion-body');
    if (!body) return;

    // Optimistic lock: set BEFORE await to prevent double-fire on rapid expand/collapse (Pitfall 4)
    accordion.dataset.loaded = '1';

    try {
        const resp = await fetch(fragmentUrl, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        body.innerHTML = await resp.text();
        // Phase 123.1-04 — Initialise deferred charts after innerHTML swap.
        // <script> tags inside innerHTML are not executed; chart data is passed
        // via data-completion-json / data-activity-json on a .sv-chart-data element.
        initFragmentCharts(body);
    } catch (_err) {
        // Reset on failure so the retry handler can re-fire the load
        accordion.dataset.loaded = '';
        body.innerHTML =
            '<div class="p-3 text-muted small">' +
            "Couldn't load — <a href=\"#\" class=\"sv-retry-section\">Retry</a>" +
            '</div>';
    }
});

// Phase 123.1 — Delegated retry handler for failed section loads (D-04).
// Clears the loaded flag and re-dispatches shown.bs.collapse on the collapse pane
// so the loader above re-fires. User sees the same spinner flow as a fresh expand.
document.addEventListener('click', (e: Event) => {
    const link = (e.target as HTMLElement).closest('.sv-retry-section');
    if (!link) return;
    e.preventDefault();

    const accordion = link.closest('[data-fragment-url]') as HTMLElement | null;
    if (!accordion) return;

    // Clear the loaded flag so the shown.bs.collapse handler will re-fetch
    accordion.dataset.loaded = '';

    const collapseEl = accordion.querySelector('.accordion-collapse');
    if (collapseEl) {
        collapseEl.dispatchEvent(new Event('shown.bs.collapse', { bubbles: true }));
    }
});
